/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useServiceContext } from '../../../../../root'
import { getMomentDateFormat } from '../../../../../utils/dateFormatting'
import { ErrorMessageStyled } from '../../../../../components/shared/ErrorMessage'
import { Select } from '../../../../../components/Select/Select'
import { getAll, type Metadata, type MetadataTableResponse } from '../../../../../services/api/metadata'
import SelectUserGroup from './SelectUserGroup'
import DatePicker from '@mdsol/lego/lib/DatePicker'

// Property types matching legacy TYPE constants
export const TYPE = {
  ARTIFACT: 'ARTIFACT',
  DATETIME: 'DATETIME',
  STRING: 'STRING',
  NUMBER: 'NUMBER',
  SELECT_ONE: 'SELECT_ONE',
  SELECT_MULTI: 'SELECT_MULTI',
  SELECT_MULTI_USER: 'SELECT_MULTI_USER',
  SELECT_MULTI_GROUP: 'SELECT_MULTI_GROUP',
  SELECT_ONE_USER: 'SELECT_ONE_USER',
  SELECT_ONE_GROUP: 'SELECT_ONE_GROUP'
}

interface PropertyItem {
  val: any
  metadataTemplateId: number | null
  property?: Metadata
  metadataTemplateType?: string
}

// Extend Metadata to include value/label for Select component compatibility
interface PropertyOption extends Metadata {
  value: number
  label: string
}

interface OptionType {
  id: number
  label: string
  value: number
  name: string
  type?: string
  definition?: {
    options?: any[] // Can be string[] or transformed option objects
  }
}

interface SetPropertyActionsProps {
  name: string
  value: PropertyItem[]
  onChange: (value: PropertyItem[]) => void
  isDisabled?: boolean
}

const SetPropertyActions: React.FC<SetPropertyActionsProps> = ({ name, value, onChange, isDisabled = false }) => {
  const context = useServiceContext()
  const user = context.service.auth.getUser?.()
  const momentDateFormat = getMomentDateFormat(user)
  const { translate } = context.service.filter

  const [properties, setProperties] = useState<PropertyOption[]>([])

  // Load all properties on mount (matching legacy behavior)
  useEffect(() => {
    const params = {
      sortBy: 'NAME',
      sortDesc: false
    }

    getAll(params)
      .then((response: MetadataTableResponse) => {
        // Store complete metadata objects with id/value/label for Select compatibility
        const options = response.elements.map((item: Metadata) => ({
          ...item,
          value: item.id,
          label: item.name
        })) as PropertyOption[]

        // Sort alphabetically by name (case-insensitive)
        const sortedOptions = options.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

        setProperties(sortedOptions)
      })
      .catch((error: any) => {
        console.error('Error loading properties:', error)
      })
  }, [])

  const addStep = () => {
    const newStep: PropertyItem = {
      val: '',
      metadataTemplateId: null
    }
    onChange([...value, newStep])
  }

  const removeStep = (index: number) => {
    const newValue = [...value]
    newValue.splice(index, 1)
    onChange(newValue)
  }

  const updateStep = (newData: PropertyItem, index: number) => {
    const newValue = [...value]
    newValue[index] = newData
    onChange(newValue)
  }

  const renderValueInput = (item: PropertyItem, index: number) => {
    if (!item.property || !item.metadataTemplateId) {
      return null
    }

    const fieldName = `${name}.${index}.val`
    const type = item.property.type

    switch (type) {
      case TYPE.DATETIME:
        return (
          <>
            {/* @ts-expect-error - DatePicker from lego doesn't have proper TypeScript types */}
            <DatePicker
              name={fieldName}
              selected={item.val}
              onChange={(val: any) => {
                item.val = val
                updateStep(item, index)
              }}
              popoverTargetOffset="3px 0"
              todayButton="Today"
              dateFormatCalendar="MMMM"
              dateFormat={momentDateFormat}
              showYearDropdown
              className="form-control field-control w100p"
              disabled={isDisabled}
            />
          </>
        )

      case TYPE.STRING:
        return (
          <>
            <input
              type="text"
              onChange={(e) => {
                item.val = e.target.value
                updateStep(item, index)
              }}
              value={item.val || ''}
              className="field-control form-control w100p"
              name={fieldName}
              maxLength={255}
              disabled={isDisabled}
            />
          </>
        )

      case TYPE.NUMBER:
        return (
          <>
            <input
              type="number"
              onChange={(e) => {
                if (e.target.value && /^[0-9]*$/.test(e.target.value)) {
                  item.val = e.target.value.length < 16 ? e.target.value : ''
                } else {
                  item.val = ''
                }
                updateStep(item, index)
              }}
              name={fieldName}
              value={item.val || ''}
              className="field-control form-control w100p"
              disabled={isDisabled}
            />
          </>
        )

      case TYPE.SELECT_ONE:
        return (
          <>
            <Select
              multi={false}
              options={(item.property.definition?.options as any) || []}
              onChange={(option: any) => {
                item.val = option ? option.value : null
                updateStep(item, index)
              }}
              value={item.val}
              name={fieldName}
              showNewOptionAtTop={true}
              className="field-control w100p"
              isDisabled={isDisabled}
            />
          </>
        )

      case TYPE.SELECT_MULTI:
        return (
          <>
            <Select
              multi={true}
              options={(item.property.definition?.options as any) || []}
              onChange={(option: any) => {
                item.val = option ? option.map((o: any) => o.value) : []
                updateStep(item, index)
              }}
              value={item.val}
              name={fieldName}
              showNewOptionAtTop={true}
              className="field-control w100p"
              isDisabled={isDisabled}
            />
          </>
        )

      case TYPE.SELECT_ONE_USER:
        return (
          <>
            <SelectUserGroup
              isMulti={false}
              value={item.val}
              onlyUsers={true}
              name={fieldName}
              onChange={(option: any) => {
                item.val = option ? option : null
                updateStep(item, index)
              }}
              isDisabled={isDisabled}
            />
          </>
        )

      case TYPE.SELECT_MULTI_USER:
        return (
          <>
            <SelectUserGroup
              isMulti={true}
              onlyUsers={true}
              value={item.val}
              name={fieldName}
              onChange={(option: any) => {
                item.val = option ? option : []
                updateStep(item, index)
              }}
              isDisabled={isDisabled}
            />
          </>
        )

      case TYPE.SELECT_ONE_GROUP:
        return (
          <>
            <SelectUserGroup
              isMulti={false}
              value={item.val}
              onlyGroups={true}
              name={fieldName}
              onChange={(option: any) => {
                item.val = option ? option : null
                updateStep(item, index)
              }}
              isDisabled={isDisabled}
            />
          </>
        )

      case TYPE.SELECT_MULTI_GROUP:
        return (
          <>
            <SelectUserGroup
              isMulti={true}
              onlyGroups={true}
              value={item.val}
              name={fieldName}
              onChange={(option: any) => {
                item.val = option ? option : []
                updateStep(item, index)
              }}
              isDisabled={isDisabled}
            />
          </>
        )

      default:
        return null
    }
  }

  return (
    <div className="form-group">
      {value.map((item, index) => (
        <div
          className="clearfix property-search-filter-row"
          key={index}
          style={{ position: 'relative', padding: '0px 10px', margin: '10px 0', width: '50%' }}
        >
          <a
            className="close-x"
            onClick={(e) => {
              e.preventDefault()
              if (isDisabled) {
                return false
              } else {
                removeStep(index)
              }
            }}
          ></a>

          <div className="inpt-wrpr mb5">
            <Select
              name={`${name}.${index}.metadataTemplateId`}
              value={item.metadataTemplateId}
              options={properties}
              placeholder={translate('SELECT_PROPERTY...')}
              onChange={(option: any) => {
                if (!option) return

                // Assign entire option as property (matches legacy behavior)
                item.metadataTemplateId = option.id
                item.property = option as Metadata
                item.metadataTemplateType = option.type
                item.val = ''

                // Transform options for SELECT_ONE and SELECT_MULTI
                if (option.type && [TYPE.SELECT_ONE, TYPE.SELECT_MULTI].indexOf(option.type) > -1) {
                  if (item.property.definition && item.property.definition.options) {
                    item.property.definition.options = (item.property.definition.options as any[]).map((i: any) => {
                      return { label: i, value: i }
                    }) as any
                  }
                }

                updateStep(item, index)
              }}
              key={index}
              searchable
              isDisabled={isDisabled}
            />
            <ErrorMessageStyled name={`${name}.${index}.metadataTemplateId`} />
          </div>

          <div>{renderValueInput(item, index)}</div>
        </div>
      ))}

      {!value || !value.length ? <ErrorMessageStyled name={name} /> : null}

      <button
        data-action={'add-property'}
        className="btn btn-chita btn-add-step btn-second at_btnAddStepApprover"
        type="button"
        onClick={addStep}
        disabled={isDisabled}
      >
        {translate('BROWSER_FILE_SETTINGS_APPROVERS_BTN_ADD_PROPERTY')}
      </button>
    </div>
  )
}

export default SetPropertyActions
